import { hasPointsToShow, takeOrderHandoff } from '@agos/customer-core'
import { T, formatCount } from '@agos/shared'
import { CheckCircle2, ChevronRight, Home, RefreshCw, Star, XCircle } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'wouter'

/**
 * The two pages a customer lands on rather than navigates to.
 *
 * Both are **order-blind**: `OnlinePaymentService.Landing` is two bare paths with no query string, so
 * neither can name the order. Success reconstructs what it can from the note the checkout left in
 * `sessionStorage` and says nothing when that is gone. They live in this module because the note is the
 * checkout's — nobody else writes it.
 *
 * The layout is legacy's: a centred card with a coloured bar across the top, a large circular icon, and
 * a *what happens next* panel. The first AGOS draft was a bare heading and two links.
 */

const CARD =
  'bg-card border border-border shadow-2xl rounded-3xl p-8 md:p-12 max-w-md w-full text-center relative overflow-hidden'
const HOME_LINK =
  'inline-flex items-center justify-center gap-2 w-full bg-secondary text-foreground hover:bg-primary hover:text-primary-foreground font-bold py-3.5 rounded-xl transition-colors'

export function SuccessPage() {
  // Read once, on mount, because reading removes it — a second read during a re-render would find
  // nothing and the banner would vanish under the customer.
  const [handoff] = useState(takeOrderHandoff)

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-4 overflow-x-hidden">
      <div className={`${CARD} space-y-6`} data-success>
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary to-amber-300" />

        <div>
          <div className="mx-auto w-20 h-20 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h1 className="text-3xl font-display font-bold mb-4">
            <T>Ďakujeme!</T>
          </h1>
          <p className="text-muted-foreground">
            <T>Vaša objednávka bola úspešne prijatá a už na nej pracujeme.</T>
          </p>
          {handoff.orderNumber !== null && (
            <p className="text-sm font-semibold mt-2" data-success-order={handoff.orderNumber}>
              {handoff.orderNumber}
            </p>
          )}
        </div>

        {hasPointsToShow(handoff) && (
          <div
            className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-left"
            data-points-earned
          >
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <Star className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="font-bold text-sm text-foreground">
                <T>Na váš účet sme pripísali</T>{' '}
                <span className="text-amber-600">
                  {formatCount(handoff.points ?? 0)} <T>bodov</T>
                </span>
              </p>
              <p className="text-xs text-muted-foreground">
                <T>Vernostné body môžete využiť pri ďalšej objednávke</T>
              </p>
            </div>
          </div>
        )}

        {/* No scratch card. `GET /mini-game/scratch-eligible` answers 404 on this backend, and the
            legacy call takes the order total from the client - whoever holds devtools tells the server
            how much they spent. An empty space is not a broken promise; a broken widget is. */}

        <div className="bg-secondary/50 rounded-2xl p-4 text-left">
          <p className="text-sm font-semibold mb-1">
            <T>Čo sa bude diať ďalej?</T>
          </p>
          <ul className="text-sm text-muted-foreground space-y-2 mt-3">
            <li className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-primary" /> <T>Kuchyňa pripravuje vaše jedlo</T>
            </li>
            <li className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-primary" />{' '}
              <T>V prípade potreby vás budeme kontaktovať</T>
            </li>
          </ul>
        </div>

        <div className="space-y-2">
          <Link href="/" data-to-home className={HOME_LINK}>
            <Home className="w-4 h-4" />
            <T>Naspäť na menu</T>
          </Link>
          <Link href="/moj-profil" data-to-profile className="block text-sm text-primary hover:underline">
            <T>Moje objednávky</T>
          </Link>
        </div>
      </div>
    </div>
  )
}

export function PaymentErrorPage() {
  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-4 overflow-x-hidden">
      <div className={CARD} data-payment-error>
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-destructive to-red-400" />

        <div className="mx-auto w-20 h-20 bg-red-100 text-red-500 rounded-full flex items-center justify-center mb-6">
          <XCircle className="w-10 h-10" />
        </div>

        <h1 className="text-3xl font-display font-bold mb-4">
          <T>Platba neúspešná</T>
        </h1>
        {/* True by construction - the bank sent the customer here - and true about the order too, because
            the checkout POSTs `/orders` before `/payments/initiate`, so it exists by the time the bank is
            involved. */}
        <p className="text-muted-foreground mb-6">
          <T>
            Platba sa nepodarila alebo bola zrušená. Vaša objednávka bola prijatá, ale zostáva nezaplatená.
          </T>
        </p>

        <div className="bg-secondary/50 rounded-2xl p-4 mb-8 text-left">
          <p className="text-sm font-semibold mb-1">
            <T>Čo môžete urobiť?</T>
          </p>
          <ul className="text-sm text-muted-foreground space-y-2 mt-3">
            {/* Legacy's first bullet advises placing the order again, which produces a *second* order
                for somebody who already has one waiting unpaid. Replaced rather than reworded: what is
                left is both true and useful. */}
            <li className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-primary shrink-0" />{' '}
              <T>Zaplaťte pri prevzatí - v hotovosti alebo kartou</T>
            </li>
            {/* Legacy's second bullet, verbatim. It had been dropped and replaced with *Ak si nie ste
                istí, zavolajte nám* - a sentence nowhere in legacy, added without a reason beside a
                replacement that had one. FB-C66. */}
            <li className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-primary shrink-0" />{' '}
              <T>Vyberte iný spôsob platby (hotovosť / terminál)</T>
            </li>
          </ul>
        </div>

        <div className="space-y-2">
          <Link href="/" data-to-home className={HOME_LINK}>
            <Home className="w-4 h-4" />
            <T>Naspäť na menu</T>
          </Link>
          <Link href="/moj-profil" data-to-profile className="block text-sm text-primary hover:underline">
            <T>Moje objednávky</T>
          </Link>
        </div>
      </div>
    </div>
  )
}
